"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import Link from "next/link"
import { ArrowRight, Clock, Code, Search, Star, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { analytics, trackEvent } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { Stagger, StaggerItem } from "@/components/motion"
import { ToolIllustration } from "@/components/tools/tool-illustration"

/**
 * Serializable subset of a ToolManifestEntry. The manifest's `icon` field is a
 * LucideIcon component and cannot cross the server→client boundary, so the
 * server page maps to this shape and icons are resolved client-side by `href`
 * via <ToolIllustration />.
 */
export interface ToolGridItem {
  id: string
  name: string
  description: string
  href: string
  estimatedTime: string
  category: string
  featured: boolean
  hasEmbed: boolean
}

const ALL = "All"

/**
 * Liquid-glass tools directory with plain-language search and category chips.
 * Categories + counts are derived from `items` in manifest order, so an empty
 * category never produces a chip.
 */
export function ToolsIndexGrid({ items }: { items: ToolGridItem[] }) {
  const [active, setActive] = useState<string>(ALL)
  const [query, setQuery] = useState("")

  // Unique categories in manifest (first-appearance) order + per-category counts.
  const { categories, counts } = useMemo(() => {
    const order: string[] = []
    const tally: Record<string, number> = {}
    for (const item of items) {
      if (!order.includes(item.category)) order.push(item.category)
      tally[item.category] = (tally[item.category] ?? 0) + 1
    }
    return { categories: order, counts: tally }
  }, [items])

  const chips = [{ label: ALL, count: items.length }, ...categories.map((c) => ({ label: c, count: counts[c] }))]
  const normalizedQuery = query.trim().toLowerCase()
  const filteredItems = useMemo(
    () => items.filter((item) => {
      const inCategory = active === ALL || item.category === active
      const matchesQuery = !normalizedQuery ||
        `${item.name} ${item.description} ${item.category}`.toLowerCase().includes(normalizedQuery)
      return inCategory && matchesQuery
    }),
    [active, items, normalizedQuery],
  )

  // One `tool_hub_search_used` per mount, on the first non-empty query. Firing
  // per keystroke would make the event a typing-speed metric, not a usage one.
  const searchFiredRef = useRef(false)
  const resultCountRef = useRef(filteredItems.length)
  resultCountRef.current = filteredItems.length
  useEffect(() => {
    if (searchFiredRef.current || !normalizedQuery) return
    searchFiredRef.current = true
    trackEvent("tool_hub_search_used", { result_count: resultCountRef.current })
  }, [normalizedQuery])

  return (
    <section id="tool-directory" className="mb-8 scroll-mt-24 md:mb-16">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h2 className="font-editorial text-2xl tracking-tight text-gray-900 md:text-3xl">Find a tool</h2>
        <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-100/80 px-3 py-1 text-xs font-bold text-green-800 md:text-sm">
          {items.length} tools
        </span>
      </div>

      <label className="relative mb-4 block max-w-2xl">
        <span className="sr-only">Search lawn care tools</span>
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" aria-hidden="true" />
        <input
          id="tools-index-search"
          type="search"
          inputMode="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by job or tool"
          aria-describedby="tools-results-count"
          aria-controls="tools-index-grid"
          className="h-12 w-full rounded-2xl border border-white/70 bg-white/70 pl-12 pr-12 text-base text-gray-900 shadow-[0_8px_24px_-18px_rgba(20,45,30,0.45)] outline-hidden backdrop-blur-md placeholder:text-gray-500 focus:border-green-500 focus:ring-2 focus:ring-green-200"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear tool search"
            className="absolute right-0.5 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-xl text-gray-500 transition-colors hover:bg-green-50 hover:text-green-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </label>

      <div
        className="scroll-hint-fade scroll-hint-fade-mobile-only mb-6 flex snap-x snap-mandatory scroll-px-4 gap-2.5 overflow-x-auto pb-1 md:flex-wrap md:overflow-visible"
        role="group"
        aria-label="Filter tools by category"
      >
        {chips.map((chip) => {
          const pressed = active === chip.label
          return (
            <button
              key={chip.label}
              type="button"
              aria-pressed={pressed}
              onClick={() => {
                setActive(chip.label)
                const nextCount = items.filter((item) => {
                  const inCategory = chip.label === ALL || item.category === chip.label
                  const matchesQuery = !normalizedQuery ||
                    `${item.name} ${item.description} ${item.category}`.toLowerCase().includes(normalizedQuery)
                  return inCategory && matchesQuery
                }).length
                trackEvent("tool_hub_filter_changed", { category: chip.label, result_count: nextCount })
              }}
              className={cn(
                "inline-flex min-h-11 shrink-0 snap-start items-center gap-2 whitespace-nowrap rounded-full px-4 text-sm font-semibold transition-[transform,background,color,border-color] duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600",
                pressed
                  ? "border border-white/40 bg-linear-to-r from-green-600 to-emerald-600 text-white shadow-[0_10px_24px_-10px_rgba(22,163,74,0.5)]"
                  : "border border-white/55 bg-white/55 text-gray-700 backdrop-blur-md hover:-translate-y-px hover:text-green-800"
              )}
            >
              {chip.label}
              <span className="text-[0.78rem] tabular-nums opacity-70">{chip.count}</span>
            </button>
          )
        })}
      </div>
      <p id="tools-results-count" className="mb-4 text-sm text-gray-600" aria-live="polite">
        Showing {filteredItems.length} {filteredItems.length === 1 ? "tool" : "tools"}
      </p>

      {/* Two-up on phones. One full-width card per row put barely one tool on
          screen at a time; ToolIndexCard drops to a compact variant under md so
          two rows fit the fold. */}
      {filteredItems.length > 0 ? (
        <Stagger id="tools-index-grid" className="grid grid-cols-2 gap-2.5 md:gap-5 lg:grid-cols-3">
        {filteredItems.map((item, index) => (
          <StaggerItem
            key={item.id}
            className="h-full"
          >
            <ToolIndexCard
              item={item}
              position={index}
              categoryFilter={active === ALL ? "all" : active}
              searchQueryPresent={normalizedQuery.length > 0}
              totalVisible={filteredItems.length}
            />
          </StaggerItem>
        ))}
        </Stagger>
      ) : (
        <div id="tools-index-grid" role="status" className="ds-glass rounded-[20px] px-6 py-10 text-center">
          <p className="font-semibold text-gray-900">No tools match that search.</p>
          <p className="mt-1 text-sm text-gray-600">Try a broader task, or clear the category filter.</p>
          <button
            type="button"
            onClick={() => { setQuery(""); setActive(ALL) }}
            className="mt-4 min-h-11 rounded-full border border-green-500/30 bg-green-100/80 px-5 text-sm font-semibold text-green-800 hover:bg-green-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
          >
            Show all tools
          </button>
        </div>
      )}
    </section>
  )
}

/**
 * Impression + click tracker for the tools-hub promo surfaces below the grid.
 * Lives here (rather than in a new file) because /tools is a server component
 * and this is the smallest client seam that keeps the promos where they are.
 *
 * Contract: same `cta_location` string on both `cta_surface_shown` and
 * `cta_clicked`, so CTR joins (see lib/analytics.ts ctaSurfaceShown).
 */
export function ToolsHubCtaTracker({
  ctaLocation,
  children,
}: {
  ctaLocation: string
  children: React.ReactNode
}) {
  const impressionRef = useImpressionEvent<HTMLDivElement>(
    ctaLocation,
    { surface: "tools_hub" },
    { emit: analytics.ctaSurfaceShown },
  )
  return (
    <div
      ref={impressionRef}
      onClick={() => trackEvent("cta_clicked", { cta_location: ctaLocation, surface: "tools_hub" })}
    >
      {children}
    </div>
  )
}

function ToolIndexCard({
  item,
  position,
  categoryFilter,
  searchQueryPresent,
  totalVisible,
}: {
  item: ToolGridItem
  position: number
  categoryFilter: string
  /** Boolean only. The raw query text is never sent. */
  searchQueryPresent: boolean
  totalVisible: number
}) {
  const { id, name, description, href, estimatedTime, category, featured, hasEmbed } = item
  return (
    <Link
      href={href}
      onClick={() => {
        trackEvent("tool_hub_card_clicked", {
          tool_id: id,
          position,
          category_filter: categoryFilter,
          search_query_present: searchQueryPresent,
          total_visible: totalVisible,
        })
      }}
      aria-label={`${name} - ${description} Estimated time: ${estimatedTime}. Category: ${category}.`}
      className={cn(
        "ds-glass group relative flex h-full flex-col gap-2 rounded-2xl p-3 transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-1 md:gap-3.5 md:rounded-[20px] md:p-[22px]",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600",
        featured && "ring-2 ring-green-500/40"
      )}
    >
      <div className="flex items-start justify-between gap-2 md:gap-3">
        <span className="grid h-10 w-10 flex-none place-items-center rounded-xl border border-white/70 bg-green-50/85 text-green-700 shadow-[0_8px_18px_-8px_rgba(22,163,74,0.3)] transition-transform duration-200 group-hover:scale-105 md:h-14 md:w-14 md:rounded-2xl">
          <ToolIllustration href={href} />
        </span>
        {/* The category chip stays on phones; "Start here" and "Embeddable" are
            md-only, because three stacked chips crowd out the name at a ~159px
            column. `truncate` is a backstop only — category labels are kept
            short enough to fit (see data/tools-manifest.ts). */}
        <span className="flex min-w-0 flex-col items-end gap-1.5">
          {featured && (
            <span className="hidden items-center gap-1 rounded-full bg-linear-to-r from-green-600 to-emerald-600 px-2.5 py-1 text-[0.72rem] font-bold text-white shadow-[0_6px_14px_-6px_rgba(22,163,74,0.5)] md:inline-flex">
              <Star className="h-3 w-3" aria-hidden="true" />
              Start here
            </span>
          )}
          <span className="inline-flex max-w-full items-center truncate rounded-full border border-green-500/25 bg-green-100/85 px-2 py-0.5 text-[0.62rem] font-bold text-green-800 md:px-2.5 md:py-1 md:text-[0.72rem]">
            {category}
          </span>
          {hasEmbed && (
            <span className="hidden items-center gap-1 rounded-full border border-[rgba(20,45,30,0.12)] bg-white/70 px-2.5 py-1 text-[0.72rem] font-bold text-gray-700 md:inline-flex">
              <Code className="h-3 w-3" aria-hidden="true" />
              Embeddable
            </span>
          )}
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-1 md:gap-1.5">
        <h3 className="text-[0.95rem] font-extrabold leading-tight tracking-[-0.02em] text-gray-900 transition-colors group-hover:text-green-700 md:text-[1.18rem]">
          {name}
        </h3>
        <p className="line-clamp-2 text-xs leading-relaxed text-gray-600 md:line-clamp-3 md:text-sm">{description}</p>
      </div>

      <div className="mt-0.5 flex items-center justify-between gap-2 border-t border-[rgba(20,45,30,0.1)] pt-2 md:mt-1 md:gap-3 md:pt-3.5">
        <span className="inline-flex items-center gap-1 text-[0.7rem] text-gray-500 md:gap-1.5 md:text-sm">
          <Clock className="h-3.5 w-3.5 md:h-4 md:w-4" aria-hidden="true" />
          {estimatedTime}
        </span>
        {/* "Try it now" is redundant on a card that is itself the link, so the
            phone keeps only the affordance arrow. */}
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700">
          <span className="hidden md:inline">Try it now</span>
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1 md:h-4 md:w-4" aria-hidden="true" />
        </span>
      </div>
    </Link>
  )
}
